/**
 * RecordingPage Component
 *
 * Main page component that handles URL parameter parsing and provider selection.
 * Supports both Dyte and LiveKit providers.
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { MeetingProviderProvider, getProviderFromURLParams } from 'contexts/MeetingProviderContext';
import { RecordingView } from 'components';
import { MeetingConfig, WatermarkConfig, WatermarkPosition } from 'types';

const DEFAULT_WATERMARK_CONFIG: WatermarkConfig = {
  url: 'https://dyte.io/images/favicon.png',
  position: WatermarkPosition.TopLeft,
  size: {
    width: 200,
  },
  opacity: 1,
  enabled: false,
};

const DEFAULT_UIKIT = false;
const DEFAULT_WAIT_TIME_MS = 60000;

const DEFAULT_MEETING_CONFIG: MeetingConfig = {
  uiKit: DEFAULT_UIKIT,
  waitTimeMs: DEFAULT_WAIT_TIME_MS,
  watermark: DEFAULT_WATERMARK_CONFIG,
};

function RecordingPage() {
  const [searchParams] = useSearchParams();
  const [classId, setClassId] = useState<string | null>(null);
  const [config, setConfig] = useState<MeetingConfig | null>(null);

  // Determine provider from URL parameter
  const provider = getProviderFromURLParams(searchParams);

  // Common parameters
  const roomName = useParams()?.roomName || (searchParams.get('roomName') as string);
  // Parent view parameters (for filtered student view)
  const source = searchParams.get('source') as string | null;
  const studentId = searchParams.get('studentId') as string | null;

  let apiBase = searchParams.get('apiBase');

  // Dyte-specific parameters
  const authToken = searchParams.get('authToken') as string;

  // LiveKit-specific parameters
  const livekitToken = searchParams.get('livekitToken') as string;
  const wsUrl = searchParams.get('wsUrl') as string;

  // CSS zoom parameter (default: 1)
  const zoom = parseFloat(searchParams.get('zoom') || '1') || 1;

  if (apiBase) {
    if (!apiBase.startsWith('https://')) {
      apiBase = `https://${apiBase}`;
    }
  }

  useEffect(() => {
    setClassId(searchParams.get('classId'));
    const configJson = searchParams.get('config');

    let parsedConfig: MeetingConfig;

    // Set defaults on config object
    if (configJson == null) {
      parsedConfig = DEFAULT_MEETING_CONFIG;
    } else {
      parsedConfig = JSON.parse(atob(configJson));
    }

    if (parsedConfig.uiKit === undefined) {
      parsedConfig.uiKit = DEFAULT_UIKIT;
    }

    if (parsedConfig.waitTimeMs === undefined) {
      parsedConfig.waitTimeMs = DEFAULT_WAIT_TIME_MS;
    }

    if (parsedConfig.watermark === undefined) {
      parsedConfig.watermark = DEFAULT_WATERMARK_CONFIG;
    } else {
      parsedConfig.watermark = {
        ...DEFAULT_WATERMARK_CONFIG,
        ...parsedConfig.watermark,
      };
    }

    setConfig(parsedConfig);
  }, [setConfig, searchParams]);

  // Validation based on provider
  if (provider === 'livekit') {
    if (!livekitToken || !wsUrl) {
      return (
        <div className='flex items-center justify-center flex-1 h-full text-white'>
          <div className='text-center'>
            <p className='text-red-500 mb-2'>Missing LiveKit Parameters</p>
            <p className='text-sm'>
              LiveKit provider requires both 'livekitToken' and 'wsUrl' query parameters.
            </p>
          </div>
        </div>
      );
    }
  } else {
    // Dyte provider (default)
    if (!authToken) {
      return (
        <div className='flex items-center justify-center flex-1 h-full text-white'>
          <div className='text-center'>
            <p className='text-red-500 mb-2'>Missing Dyte Parameters</p>
            <p className='text-sm'>authToken not provided in query parameters!</p>
          </div>
        </div>
      );
    }
  }

  if (config == null) {
    return <p>Initializing.....</p>;
  }

  return (
    <div
      style={{
        zoom: zoom,
        height: `${100 / zoom}vh`,
        width: `${100 / zoom}vw`,
        overflow: 'hidden',
      }}
    >
      <MeetingProviderProvider provider={provider}>
        <RecordingView
          roomName={roomName}
          authToken={authToken}
          config={config}
          classId={classId}
          apiBase={apiBase}
          zoom={zoom}
          // Parent view props
          source={source}
          studentId={studentId}
          // LiveKit-specific props
          livekitToken={livekitToken}
          wsUrl={wsUrl}
        />
      </MeetingProviderProvider>
    </div>
  );
}

export default RecordingPage;
